<template>
  <div v-if="resetRouteActive">
    <router-view />
  </div>
  <v-app
    v-else
    :class="{
      'overlay-opened': overlayOpened,
      'ai-agent-opened': aiAgentOpened,
      'visible-scroll': analyticsFilterOpened
    }"
    @resize="zoomFix"
  >
    <AccessDeniedDialog />
    <v-main
      v-if="!authRouteActive"
      class="main"
    >
      <router-view />
    </v-main>
    <router-view v-else />
    <Snackbar />
  </v-app>
</template>

<script>
import { camelCase } from '@/libs/lodash'
import { Chart } from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'
import AccessDeniedDialog from './components/AccessDeniedDialog'
import { useGeneralStore } from '@/store/pinia/generalStore'
import { useAiAgentStore } from '@/store/pinia/aiAgentStore'
import { useAnalyticsFiltersStore } from '@/store/pinia/analyticsFiltersStore'
import { zoomFix } from '@/libs/zoomFix'
import Snackbar from '@/components/Snackbar.vue'

import { useFiltersStore } from '@/store/pinia/filtersStore'
import { isRedirectionForDocs, redirectToDocs } from '@/auth/redirectionForDocs'
import { getRefreshToken } from '@/auth/tokenService'

export default {
  components: {
    Snackbar,
    AccessDeniedDialog,
  },
  setup () {
    return {
      filtersStore: useFiltersStore(),
      analyticsFiltersStore: useAnalyticsFiltersStore(),
      generalStore: useGeneralStore(),
      aiAgentStore: useAiAgentStore(),
    }
  },
  computed: {
    resetRouteActive () {
      return this.routeName === 'reset-me'
    },
    appConfig () {
      return this.generalStore.appConfig
    },
    aiAgentOpened () {
      return this.aiAgentStore.aiAgentOpened
    },
    aiAgentWindowWidth () {
      return this.aiAgentStore.aiAgentWindowWidth
    },
    filtersConfig () {
      return this.appConfig?.page_filters || null
    },
    routeName () {
      return this.$route?.name || ''
    },
    authRouteActive () {
      return this.routeName.includes('login') ||
        this.routeName.includes('forgot-password') ||
        this.routeName.includes('resend-token') ||
        this.routeName.includes('reset-password')
    },
    overlayOpened () {
      return this.filtersStore.filtersOpened || this.analyticsFiltersStore.filtersOpened
    },
    analyticsFilterOpened () {
      return this.analyticsFiltersStore.filtersOpened
    },
  },
  watch: {
    $route (to) {
      this.setCurrentPageFilters(to)
    },
    aiAgentOpened () {
      this.updateFiltersMargin()
    },
    aiAgentWindowWidth () {
      this.updateFiltersMargin()
    },
  },
  async created () {
    // Check if we need to show the access denied dialog
    // This is set by the router navigation guard when access is denied
    if (localStorage.getItem('show_access_denied_dialog') === 'true') {
      // Remove the flag from localStorage
      localStorage.removeItem('show_access_denied_dialog')

      // Show the dialog using the store
      this.generalStore.setAccessDeniedDialogVisible(true)
    }

    Chart.register(annotationPlugin)
    Chart.register({
      id: 'zoomCSS',
      beforeEvent (chart, {event}) {
        const zoom = document.documentElement.style.getPropertyValue('--zoom') || 1
        if (zoom !== 1) {
          // TODO:Vu3 do we need the native prop here?
          event.x = Math.round(event.native.offsetX / zoom)
          event.y = Math.round(event.native.offsetY / zoom)
        }
      }
    }
    )
    // If the user is logged in, start the interval to refresh the tokens and config
    this.zoomFix()
    window.onresize = this.zoomFix

    // Proactively refresh once on boot when a refresh token exists (covers an
    // expired access token). Ongoing refreshes are handled reactively/proactively
    // by the axios interceptors — no polling interval.
    if (
      this.routeName !== 'reset-password' &&
      getRefreshToken()
    ) {
      await this.$auth.refreshTokens()
    }
    if (this.$auth.isAccessTokenValid()) {
      // This is the second place we check for the docs redirection
      // for the cases when the user is logged in and still used redirection from docs
      // This call changes window location if needed, so no need to handle termination
      this.handleDocsRedirection()
    }

    await this.$auth.initAppConfig()
    setInterval(()=> {
      this.$auth.checkAppFullConfigExpiration()
      this.$auth.checkAppPartialConfigExpiration()
    }, 1000 * 60)
    this.setCurrentPageFilters(this.$route)
    // TODO explain why this is here
    localStorage.removeItem('cubeBrowserData')
    this.startHardRefreshListener()
  },
  methods: {
    zoomFix,
    updateFiltersMargin () {
      if (this.aiAgentOpened) {
        document.documentElement.style.setProperty('--filters-margin-right', this.aiAgentWindowWidth + 'px')
      }
    },
    // Sometimes the remote config state drift from the local config
    // For example, multiple users modifying the same organization resource
    // so we add the ability to use hard refresh, which will delete the existing config
    // and will cause the next load do download the fresh config object
    startHardRefreshListener () {
      const deleteConfig = () => this.$auth.deleteAppConfig()
      document.addEventListener('keydown', e => {
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
          deleteConfig()
        }
      })
    },
    setCurrentPageFilters (pageRoute) {
      if (!this.filtersConfig) {
        return
      }
      const pathParts = pageRoute.path.split('/').filter(p => p.length)
      const pagesConfig = this.filtersConfig[pathParts[0]]
      let pageFiltersToSet = []

      if (pagesConfig) {
        const pageFilters = pagesConfig[pathParts.slice(1).join(' / ')]

        if (pageFilters && pageFilters.length) {
          pageFiltersToSet = pageFilters
        }
      }

      this.filtersStore.setCurrentPageFilters(pageFiltersToSet.map(camelCase))
    },
    handleDocsRedirection () {
      const redirect = this.$route.query['redirect']?.toString() || ''
      if (isRedirectionForDocs(redirect)) {
        return redirectToDocs(redirect)
      }
    }
  }
}
</script>

<style lang="scss">
.main {
  background-color: #f9f9f9;
}
.v-application {
  &.overlay-opened:not(.visible-scroll){
    overflow: hidden;
    height: calc(100vh / var(--zoom));
  }
  &.ai-agent-opened {
    .filter-widget-card {
      margin-right: var(--filters-margin-right);
    }
  }
}
.main-layout-wrapper {
  margin-left: var(--ts-content-left);
}
// Bound to Vuetify 4's md breakpoint (840px), which is where the sidebar rail
// starts showing via d-md-block. A wider bound paints the rail with no matching
// content offset, and page content covers it.
@media (max-width: 839.98px) {
  .main-layout-wrapper {
    margin-left: var(--ts-page-gap);
  }
}
</style>
